import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";

export interface GreenhouseTransferBedDto {
  greenhouseBedId: string;
  trayCount: number;
}

export interface GreenhouseTransferBedDetailDto {
  greenhouseBedId: string;
  bedName: string;
  bayName: string;
  trayCount: number;
}

export interface GreenhouseTransferRequestDto {
  id: string;
  greenhouseStockEntryId: string;
  cropTypeName: string;
  variety?: string;
  trayType?: string;
  sourceGreenhouseId: string;
  sourceGreenhouseName: string;
  destinationGreenhouseId: string;
  destinationGreenhouseName: string;
  trayCount: number;
  reason: string;
  status: string;
  requestedByUserId: string;
  requestedByUserName: string;
  requestedAt: string;
  decisionByUserId?: string;
  decisionByUserName?: string;
  decisionAt?: string;
  decisionComment?: string;
  transferredAt?: string;
  sourceBeds: GreenhouseTransferBedDetailDto[];
  destinationBeds: GreenhouseTransferBedDetailDto[];
  createdAt: string;
  workflowVersion: number;
  rowVersion: number;
  fulfilmentStatus?: string;
}

export interface PagedGreenhouseTransfers {
  items: GreenhouseTransferRequestDto[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
}

export interface CreateGreenhouseTransferRequestDto {
  greenhouseStockEntryId: string;
  destinationGreenhouseId: string;
  transferTrayCount: number;
  sourceBeds: GreenhouseTransferBedDto[];
  destinationBeds: GreenhouseTransferBedDto[];
  reason: string;
}

export interface ApproveGreenhouseTransferRequestDto {
  rowVersion?: number;
  decisionComment?: string;
}

export interface RejectGreenhouseTransferRequestDto {
  rowVersion?: number;
  decisionComment: string;
}

export const greenhouseTransfersApi = {
  getAll: async (pageNumber = 1, pageSize = 20): Promise<PagedGreenhouseTransfers> => {
    const { data } = await apiClient.get<ApiResponse<PagedGreenhouseTransfers>>(
      "/greenhouse-transfers", { params: { pageNumber, pageSize } }
    );
    return data.data;
  },

  getById: async (id: string): Promise<GreenhouseTransferRequestDto> => {
    const { data } = await apiClient.get<ApiResponse<GreenhouseTransferRequestDto>>(
      `/greenhouse-transfers/${id}`
    );
    return data.data;
  },

  getPending: async (pageNumber = 1, pageSize = 20): Promise<PagedGreenhouseTransfers> => {
    const { data } = await apiClient.get<ApiResponse<PagedGreenhouseTransfers>>(
      "/greenhouse-transfers/pending", { params: { pageNumber, pageSize } }
    );
    return data.data;
  },

  getByStockEntry: async (stockEntryId: string, pageNumber = 1, pageSize = 20): Promise<PagedGreenhouseTransfers> => {
    const { data } = await apiClient.get<ApiResponse<PagedGreenhouseTransfers>>(
      `/greenhouse-transfers/by-entry/${stockEntryId}`, { params: { pageNumber, pageSize } }
    );
    return data.data;
  },

  request: async (payload: CreateGreenhouseTransferRequestDto): Promise<GreenhouseTransferRequestDto> => {
    const { data } = await apiClient.post<ApiResponse<GreenhouseTransferRequestDto>>(
      "/greenhouse-transfers",
      payload
    );
    return data.data;
  },

  approve: async (id: string, payload: ApproveGreenhouseTransferRequestDto): Promise<GreenhouseTransferRequestDto> => {
    const { data } = await apiClient.post<ApiResponse<GreenhouseTransferRequestDto>>(
      `/greenhouse-transfers/${id}/approve`,
      payload
    );
    return data.data;
  },

  reject: async (id: string, payload: RejectGreenhouseTransferRequestDto): Promise<GreenhouseTransferRequestDto> => {
    const { data } = await apiClient.post<ApiResponse<GreenhouseTransferRequestDto>>(
      `/greenhouse-transfers/${id}/reject`,
      payload
    );
    return data.data;
  },
};
